import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AILeadDetailDrawer from "@/components/AILeadDetailDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Archive, RefreshCw, RotateCcw, Trash2, Phone, MessageSquare, Search,
} from "lucide-react";

// Archived Leads — read-only view of soft-deleted leads. Rep can:
//   • Open the lead's transcripts / SMS / calls (drawer stays untouched)
//   • Restore the lead back to the active pipeline
//   • Purge permanently (compliance / GDPR — irreversible)
// Every Retell recording + SMS + call event stays attached even in this
// state so audit-trail requirements are met.

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

export default function ArchivedLeads() {
  const [rows, setRows]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [filter, setFilter] = useState("");
  const [openLeadId, setOpenLeadId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/leads/archived");
      setRows(data?.rows || []);
    } catch { setRows([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.phone || "").includes(q) ||
      (r.property_address || "").toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const restore = async (id, name) => {
    if (!window.confirm(`Restore ${name || "this lead"} to the active pipeline?`)) return;
    try {
      await api.post(`/leads/${id}/restore`);
      toast.success("Lead restored to Pipeline");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const purge = async (id, name) => {
    const confirmed = window.prompt(
      `PERMANENTLY delete ${name || "this lead"} and every attached recording, transcript, and SMS?\n\n` +
      `This CANNOT be undone. Type PURGE to confirm.`,
    );
    if (confirmed !== "PURGE") {
      toast.info("Purge cancelled");
      return;
    }
    try {
      await api.delete(`/leads/${id}/purge`);
      toast.success("Lead permanently purged");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="archived-leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Archive className="text-slate-500" size={24} /> Archived Leads
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            Soft-deleted leads. Every Retell recording and SMS stays attached, so you can still open the record. Restore returns it to Pipeline. Purge is permanent.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid="archived-refresh" className="rounded-full">
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search name, phone, address…"
                 value={filter}
                 onChange={(e) => setFilter(e.target.value)}
                 className="pl-8 h-8 text-xs"
                 data-testid="archived-filter" />
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} archived
        </div>
      </div>

      <div className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50 px-4 py-2.5 border-b">
          <div className="col-span-4">Lead / Property</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Archived</div>
          <div className="col-span-2">History</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {filtered.length === 0 && !busy && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            {rows.length === 0
              ? "No archived leads yet. When you delete a lead from Pipeline, it lands here and stays for restore or purge."
              : "No archived leads match your filter."}
          </div>
        )}
        {filtered.map((r) => (
          <div key={r.id}
               className="grid grid-cols-12 items-center px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition"
               data-testid={`archived-row-${r.id}`}>
            <div className="col-span-4 cursor-pointer" onClick={() => setOpenLeadId(r.id)}>
              <div className="font-semibold text-sm truncate">{r.name || "Unknown"}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {r.property_address || "—"}
              </div>
            </div>
            <div className="col-span-2 text-xs text-slate-700">{r.phone || "—"}</div>
            <div className="col-span-2 text-xs text-slate-700">{fmtDate(r.deleted_at)}</div>
            <div className="col-span-2 flex items-center gap-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1"
                    title={`${r.retell_call_count} AI calls`}>
                <Phone size={11} className="text-purple-500" />
                {r.retell_call_count}
              </span>
              <span className="inline-flex items-center gap-1"
                    title={`${r.sms_count} SMS`}>
                <MessageSquare size={11} className="text-emerald-500" />
                {r.sms_count}
              </span>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1">
              <Button size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); restore(r.id, r.name); }}
                      className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`archived-restore-${r.id}`}>
                <RotateCcw size={11} className="mr-1" /> Restore
              </Button>
              <Button size="sm" variant="ghost"
                      onClick={(e) => { e.stopPropagation(); purge(r.id, r.name); }}
                      className="h-7 text-[11px] text-red-600 hover:bg-red-50"
                      data-testid={`archived-purge-${r.id}`}
                      title="Permanently delete (irreversible)">
                <Trash2 size={11} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AILeadDetailDrawer
        leadId={openLeadId}
        onClose={() => setOpenLeadId(null)}
      />
    </div>
  );
}
